/**
 * Agent Usage Chart Component
 *
 * Displays top 10 agents by usage count in a horizontal bar chart.
 * Shows which agents are being used most frequently.
 */

import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { AgentUsageStats } from '../../../../../types/metrics';

interface AgentUsageChartProps {
  data: AgentUsageStats[];
}

const COLORS = ['#F4CC04', '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6', '#6366f1', '#f97316'];

export const AgentUsageChart: React.FC<AgentUsageChartProps> = ({ data }) => {
  // Take top 10 agents by usage count
  const top10Agents = [...data].sort((a, b) => b.usage_count - a.usage_count).slice(0, 10);

  // Format data for recharts
  const chartData = top10Agents.map((agent, index) => ({
    name: agent.agent_name.length > 35 ? agent.agent_name.substring(0, 35) + '...' : agent.agent_name,
    fullName: agent.agent_name,
    usage: agent.usage_count,
    color: COLORS[index % COLORS.length],
  }));

  return (
    <div className="bg-[#2a2a2a] rounded-xl p-6 border border-[#333333] h-[442px]">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-white">Agentes mais utilizados</h3>
      </div>

      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={330}>
          <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#333333" />
            <XAxis type="number" stroke="#888888" style={{ fontSize: '12px' }} />
            <YAxis
              dataKey="name"
              type="category"
              stroke="#888888"
              style={{ fontSize: '11px' }}
              width={200}
              tick={{ fill: '#888888' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #333333',
                borderRadius: '8px',
                color: '#ffffff',
                padding: '8px 12px',
              }}
              content={({ payload }) => {
                if (!payload || payload.length === 0) return null;
                const data = payload[0].payload;
                const fullName = data.fullName.length > 50 ? data.fullName.substring(0, 50) + '...' : data.fullName;
                return (
                  <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #333333', borderRadius: '8px', padding: '8px 12px' }}>
                    <p style={{ margin: 0, color: '#ffffff', fontSize: '14px' }}>
                      {fullName}: {data.usage} usos
                    </p>
                  </div>
                );
              }}
            />
            <Bar dataKey="usage" radius={[0, 4, 4, 0]} animationDuration={500}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="flex items-center justify-center h-[330px] text-[#666666]">
          Nenhum agente foi utilizado no período
        </div>
      )}
    </div>
  );
};
